<template>
  <div class="game-card" @click="goDetail">
    <div class="game-card__header">
      <div class="game-card__round overflow-text">{{ roundTitle || '—' }}</div>
      <div v-if="showLiveBadge" class="game-card__badge" :class="liveBadgeClass">
        <img :src="liveBadgeIcon" alt="" />
        <span>{{ liveBadgeText }}</span>
      </div>
      <span v-else class="game-card__status">{{ statusText }}</span>
    </div>

    <div class="game-card__body">
      <div class="game-card__meta">
        <div class="game-card__time">{{ timeText || '—' }}</div>
        <div class="game-card__date">{{ dateText }}</div>
        <div class="game-card__venue overflow-text">{{ venueText || '—' }}</div>
      </div>
      <div class="game-card__teams">
        <div class="team-row overflow-text">{{ info.homeTeamName || '—' }}</div>
        <div class="team-spacer" aria-hidden="true"></div>
        <div class="team-row team-row--away overflow-text">{{ info.awayTeamName || '—' }}</div>
      </div>
      <div v-if="showScoreColumn" class="game-card__score">
        <div v-if="hasSubScores" class="sub-score-grid">
          <div
            v-for="(score, index) in formatSubScore(info.subScore)"
            :key="index"
            class="sub-score-col"
          >
            <div class="sub-score-item" :class="scoreSideClass(score, 'home')">{{ score.homeScore }}</div>
            <div class="sub-score-item" :class="scoreSideClass(score, 'away')">{{ score.awayScore }}</div>
          </div>
        </div>
        <div v-else-if="hasTotalScore && unitType === 0" class="total-score">
          <div class="sub-score-item" :class="totalScoreClass('home')">{{ info.homeScore }}</div>
          <div class="sub-score-item" :class="totalScoreClass('away')">{{ info.awayScore }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Moment from 'moment'

export default {
  props: {
    info: {
      type: Object,
      default: () => ({})
    },
    unitType: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      mp4: 'https://images.snsports.cn/FuLkSv4qR0RynUd9u0wOq-9oA-dO',
      live: 'https://images.snsports.cn/FpZC7H9t4rYk2Lr5xJZ1_Rn8bJm1'
    }
  },
  computed: {
    roundTitle() {
      const parts = [this.info.groupName, this.info.roundDescription]
        .map(v => (v == null ? '' : String(v).trim()))
        .filter(Boolean)
      return parts.join(' ')
    },
    liveStatusCode() {
      const ls = this.info.liveStatus
      if (ls == null || ls === '') return null
      if (typeof ls === 'number') return ls
      if (typeof ls === 'object' && ls.status != null) return Number(ls.status)
      const n = Number(ls)
      return Number.isNaN(n) ? null : n
    },
    showLiveBadge() {
      if (this.info.livePublic !== 1) return false
      const code = this.liveStatusCode
      return code === 1 || code === 2
    },
    liveBadgeText() {
      if (this.liveStatusCode === 1) return '直播中'
      if (this.liveStatusCode === 2) return '录像'
      return ''
    },
    liveBadgeClass() {
      if (this.liveStatusCode === 1) return 'badge-live'
      if (this.liveStatusCode === 2) return 'badge-replay'
      return ''
    },
    liveBadgeIcon() {
      return this.liveStatusCode === 2 ? this.mp4 : this.live
    },
    gameStatusCode() {
      const raw = this.info.gameStatus
      if (raw === '' || raw == null) return null
      const n = Number(raw)
      return Number.isNaN(n) ? null : n
    },
    hasTotalScore() {
      return this.info.homeScore > -1 && this.info.awayScore > -1
    },
    hasSubScores() {
      return this.hasTotalScore && this.unitType !== 0 && this.formatSubScore(this.info.subScore).length > 0
    },
    showScoreColumn() {
      return this.hasSubScores || (this.hasTotalScore && this.unitType === 0)
    },
    timeText() {
      if (this.info.beginDateMode === 1) return '接上一场'
      if (!this.info.beginDate) return ''
      const m = Moment(this.info.beginDate)
      if (!m.isValid()) return ''
      if (this.info.beginDateMode === 2) {
        return `不早于 ${m.format('HH:mm')}`
      }
      return m.format('HH:mm')
    },
    dateText() {
      if (!this.info.beginDate || this.info.beginDateMode === 1) return ''
      const m = Moment(this.info.beginDate)
      return m.isValid() ? m.format('YY/M/D') : ''
    },
    venueText() {
      const name = this.info.umFieldName || this.info.fieldName || this.info.courtName || ''
      if (!name) return ''
      return String(name).includes('场地') ? name : `${name}号场地`
    },
    statusText() {
      if (this.info.livePublic === 1) {
        const code = this.liveStatusCode
        if (code === -3 || code === 0) return '即将直播'
      }
      const code = this.gameStatusCode
      if (code === 2) return '已完赛'
      if (code === 1) return '进行中'
      if (this.hasTotalScore || this.hasSubScores) return '已完赛'
      if (code === 0) return '未开始'
      return '未开始'
    }
  },
  methods: {
    formatSubScore(subScore) {
      if (!subScore) return []
      return subScore.split(',')
        .map(score => {
          const [homeScore, awayScore] = score.split(':').map(Number)
          if (homeScore === -1 || awayScore === -1) return null
          let win = 'draw'
          if (homeScore > awayScore) win = 'home'
          else if (homeScore < awayScore) win = 'away'
          return { homeScore, awayScore, win }
        })
        .filter(Boolean)
    },
    scoreSideClass(score, side) {
      if (score.win === 'draw') return 'is-neutral'
      if (side === 'home') return score.win === 'home' ? 'is-win' : 'is-lose'
      return score.win === 'away' ? 'is-win' : 'is-lose'
    },
    totalScoreClass(side) {
      if (!this.hasTotalScore) return 'is-neutral'
      if (this.info.homeScore === this.info.awayScore) return 'is-neutral'
      if (side === 'home') {
        return this.info.homeScore > this.info.awayScore ? 'is-win' : 'is-lose'
      }
      return this.info.awayScore > this.info.homeScore ? 'is-win' : 'is-lose'
    },
    getLiveStatus() {
      if (this.info.livePublic !== 1) return ''
      const code = this.liveStatusCode
      if (code === 2) return 'mp4'
      if (code === 1) return 'live'
      return ''
    },
    goDetail() {
      if (this.hasTotalScore || this.getLiveStatus() !== '') {
        this.$router.push(`/game/${this.info.id}/data`)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.overflow-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.game-card {
  margin-bottom: 12px;
  padding: 10px 12px 12px;
  border-radius: 5px;
  background: #f9f9f9;
  box-sizing: border-box;
}

.game-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
  margin-bottom: 8px;
}

.game-card__round {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #303133;
  line-height: 20px;
}

.game-card__status {
  flex-shrink: 0;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f2f3f5;
  font-size: 12px;
  color: #909399;
  line-height: 18px;
  white-space: nowrap;
}

.game-card__badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 8px;
  height: 20px;
  padding: 0 6px;
  border-radius: 2px;
  font-size: 11px;
  color: #fff;

  img {
    width: 12px;
    height: 12px;
    margin-right: 4px;
  }
}

.badge-live {
  background: #67c23a;
}

.badge-replay {
  background: #909399;
}

.game-card__body {
  display: flex;
  align-items: flex-start;
}

.game-card__meta {
  width: 56px;
  flex-shrink: 0;
}

.game-card__time {
  height: 22px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 22px;
}

.game-card__date {
  height: 16px;
  font-size: 12px;
  color: #303133;
  line-height: 16px;
}

.game-card__venue {
  height: 16px;
  font-size: 12px;
  color: #909399;
  line-height: 16px;
}

.game-card__teams {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
  display: flex;
  flex-direction: column;
}

.team-row {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 22px;
  height: 22px;

  &--away {
    height: 16px;
    line-height: 16px;
  }
}

.team-spacer {
  height: 16px;
  flex-shrink: 0;
}

.game-card__score {
  flex-shrink: 0;
  margin-left: 10px;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.sub-score-grid {
  display: flex;
  align-items: center;

  .sub-score-col + .sub-score-col {
    margin-left: 4px;
  }
}

.sub-score-col {
  display: flex;
  flex-direction: column;

  .sub-score-item + .sub-score-item {
    margin-top: 6px;
  }
}

.total-score {
  display: flex;
  flex-direction: column;

  .sub-score-item + .sub-score-item {
    margin-top: 6px;
  }
}

.sub-score-item {
  min-width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  padding: 0 2px;
  box-sizing: border-box;

  &.is-win {
    background: #ecf5ff;
    color: #337ecc;
  }

  &.is-lose,
  &.is-neutral {
    background: #e9e9eb;
    color: #909399;
  }
}
</style>
